import { useEffect, useState, useCallback } from 'react'
import {
  Form,
  FormInstance,
  Row,
  Col,
  Button,
  Card,
  Radio,
  Table,
  Tag,
  Space,
  Divider,
} from 'antd'

import { SearchSelect } from '@components/User/Form'
import { CloseOutlined, CopyOutlined } from '@ant-design/icons'
import { api } from '@helpers/api'
import { useTranslation } from 'react-i18next'
import FormItemsOrderRequest from '../FormItemsOrderRequest'
import { IContract, IItem } from '@/types/IOrder'
import { formatItems } from '@helpers/Order/formatOrderItem'
import { fetchProductData } from '@helpers/Order/fetchProductData'
import { fillOrderItems } from '@helpers/Order/fillOrderItems'
import { useGenericContext } from '@hooks/useGenericContext'
import { OrderContext } from '@contexts/OrderContext'
import { formatRelatedProducts } from '@helpers/Order/formatRelatedProducts'
import Descriptions from '@components/common/Descriptions'
import { translateColumns } from '@helpers/translateColumns'
import {
  itemsColumns,
  productColumns,
  relatedProductsColumns,
  relatedProductColumns,
  transformOrderRequest,
} from '@pages/Order'
import { CustomDescriptionType } from '@/types/IDescriptions'
import Title from '@components/common/Title'

const APP_URL = import.meta.env.VITE_OPEN_URL

interface IFormItems {
  form: FormInstance
  subtotals: number[]
  setSubtotals: (subtotals: number[]) => void
  debouncedCalculateOrderPrice: (values: any) => void
}

const FormItems = ({
  form,
  subtotals,
  setSubtotals,
  debouncedCalculateOrderPrice,
}: IFormItems) => {
  const { t } = useTranslation()
  const device = Form.useWatch('device')
  const { data, action } = useGenericContext(OrderContext)
  const [url, setUrl] = useState('product?')
  const [contract, setContract] = useState<IContract | null>(null)

  const itemsColumnsTranslate = translateColumns(itemsColumns, t)
  const relatedProductsColumnsTranslate = translateColumns(
    relatedProductsColumns,
    t,
  )
  const relatedProductColumnsTranslate = translateColumns(
    relatedProductColumns,
    t,
  )

  useEffect(() => {
    const fetchContracts = async () => {
      const result = await fillOrderItems(
        device,
        form,
        setUrl,
        debouncedCalculateOrderPrice,
      )
      if (result) {
        setContract(result)
      }
    }

    if (device?.length) {
      fetchContracts()
    }
  }, [device, form, debouncedCalculateOrderPrice])

  const handleOnChange = useCallback(
    (value: string | null, itemIndex: number) => {
      if (!value) {
        const currentItems = form.getFieldValue('items') ?? []
        const updatedItems = currentItems.map((item: IItem, index: number) => {
          if (index === itemIndex) {
            return {
              ...item,
              product: { uuid: null },
              order_request: { items: [], related_products: [] },
            }
          }
          return item
        })
        form.setFieldsValue({ items: updatedItems })
        debouncedCalculateOrderPrice(form.getFieldsValue())
        return
      }

      api.get(`/product/${value}`).then((result) => {
        const productData = result.data?.data?.[0] ?? result.data

        if (!productData?.uuid) {
          return
        }

        const formattedProductItems = formatItems(productData.items)
        const formattedRelatedProducts = formatRelatedProducts(
          productData.related_products,
        )
        const currentItems = form.getFieldValue('items') ?? []
        const updatedItems = currentItems.map((item: IItem, index: number) => {
          if (index === itemIndex) {
            return {
              ...item,
              product: {
                uuid: productData.uuid,
                type: productData.type,
                dc: productData.dc,
                device: undefined,
              },
              order_request: {
                items: formattedProductItems,
                related_products: formattedRelatedProducts,
              },
            }
          }
          return item
        })

        form.setFieldsValue({ items: updatedItems })
        debouncedCalculateOrderPrice(form.getFieldsValue())
      })
    },
    [form, debouncedCalculateOrderPrice],
  )

  const handleDuplicateItem = useCallback(
    (indexToDuplicate: number) => {
      const items = form.getFieldValue('items') ?? []
      const itemToDuplicate = items[indexToDuplicate]

      if (itemToDuplicate) {
        const duplicatedItem = JSON.parse(JSON.stringify(itemToDuplicate))
        if (duplicatedItem.product) {
          duplicatedItem.product.device = undefined
        }

        form.setFieldsValue({ items: [...items, duplicatedItem] })
        debouncedCalculateOrderPrice(form.getFieldsValue())
      }
    },
    [form, debouncedCalculateOrderPrice],
  )

  const handleRemoveItem = useCallback(
    (indexToRemove: number, removeAnt: (index: number) => void) => {
      removeAnt(indexToRemove)
      setSubtotals((prev) => prev.filter((_, i) => i !== indexToRemove))
      setTimeout(() => {
        debouncedCalculateOrderPrice(form.getFieldsValue(true))
      }, 0)
    },
    [setSubtotals, debouncedCalculateOrderPrice, form],
  )

  const formatSubtotal = (subtotal: number | undefined): string => {
    return (
      subtotal?.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) ?? '0.00'
    )
  }

  const addNewProduct = useCallback(() => {
    fetchProductData().then((product) => {
      const currentItems = form.getFieldValue('items') ?? []

      if (product) {
        const formattedDefaultItems = formatItems(product.items)
        const formattedDefaultRelatedProducts = formatRelatedProducts(
          product.related_products,
        )
        form.setFieldsValue({
          items: [
            ...currentItems,
            {
              product: {
                uuid: product.uuid,
                dc: product.dc,
                type: product.type,
                device: undefined,
              },
              order_request: {
                items: formattedDefaultItems,
                related_products: formattedDefaultRelatedProducts,
              },
            },
          ],
        })
      } else {
        form.setFieldsValue({
          items: [
            ...currentItems,
            {
              product: { uuid: null },
              order_request: {
                items: [],
                related_products: [],
              },
            },
          ],
        })
      }
      debouncedCalculateOrderPrice(form.getFieldsValue())
    })
  }, [form, debouncedCalculateOrderPrice])

  const renderProductCard = (field: any, index: number) => {
    const itemData = form.getFieldValue(['items', field.name])
    const productData = itemData?.product
    const dcData = productData?.dc
    const iconName = dcData?.shortname?.substring(0, 3).toLowerCase() ?? ''

    return (
      <Col xs={24} xl={12} key={field.key}>
        <span>{t('PRODUCTS')}</span>
        <Card
          style={{ width: '100%' }}
          type="inner"
          size="small"
          title={`${t('PRODUCT')} ${index + 1}`}
          extra={
            <>
              {action !== 'change' && !data?.device ? (
                <>
                  <Button
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => handleDuplicateItem(index)}
                    data-cy="CopyOutlined"
                    title="Duplicate Product"
                  />
                  <CloseOutlined
                    onClick={() => handleRemoveItem(index, field.remove)}
                    data-cy="RemoveProduct"
                  />
                </>
              ) : null}
            </>
          }
        >
          <Row gutter={[16, 0]}>
            <Col span={18} xl={18} xs={24}>
              <Form.Item
                {...field.restField}
                name={[field.name, 'product', 'uuid']}
                rules={[{ required: true, message: 'Product is required!' }]}
                noStyle
              >
                <SearchSelect
                  searchUrl={url}
                  placeholder={t('PRODUCT')}
                  onChange={(value) => handleOnChange(value, index)}
                  data-cy={`select-product-${index}`}
                  allowClear
                />
              </Form.Item>
            </Col>

            {productData?.uuid && (
              <Col xl={{ span: 4, offset: 2 }} xs={24}>
                <Form.Item name={[field.name, 'datacenter']} label={'Datacenter'}>
                  <Radio.Group optionType="button" buttonStyle="solid">
                    <Radio>
                      {dcData && (
                        <>
                          <img
                            src={`${APP_URL}/assets/images/dc/${iconName}.png`}
                            alt="DC Icon"
                            style={{
                              width: '25px',
                              height: '15px',
                              marginRight: '8px',
                              verticalAlign: 'middle',
                            }}
                          />
                          {dcData.shortname}
                        </>
                      )}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            )}

            {productData?.uuid && (
              <Col span={24} style={{ marginTop: '10px' }}>
                <FormItemsOrderRequest name={field.name} form={form} />
              </Col>
            )}
          </Row>
          {subtotals[index] !== undefined && (
            <Button
              disabled
              style={{
                pointerEvents: 'none',
                textAlign: 'center',
                color: '#389e0d',
                backgroundColor: '#f6ffed',
              }}
              data-cy="subtotal"
            >
              SUBTOTAL: {formatSubtotal(subtotals[index])}
            </Button>
          )}
        </Card>
      </Col>
    )
  }

  const renderContractDetails = () => {
    if (!contract) return null

    return (
      <Col xs={24} xl={12}>
        <Title>{t('CONTRACT')}</Title>
        <Descriptions
          columns={productColumns}
          bordered
          dataSource={contract?.product}
          size="small"
          column={{ xs: 1, xl: 4 }}
        />
        <Table
          dataSource={transformOrderRequest(contract?.order_request).items}
          columns={itemsColumnsTranslate}
          rowKey="uuid"
          pagination={false}
        />
        <Descriptions
          columns={total}
          bordered
          dataSource={contract?.order_request}
          size="small"
          column={{ xs: 1, xl: 4 }}
        />
        <Divider />
        <Space>
          <Title>{t('SUPPORT_LEVEL')}</Title>
          <Tag color="blue">{contract.order_request.support_level}</Tag>
        </Space>
        {contract?.order_request?.items?.some((item) => item.item?.related_product) && (
          <>
            <Divider />
            <Space style={{ marginBottom: '10px' }}>
              <Title>{t('RELATED_PRODUCT')}</Title>
              {contract.order_request.items
                .filter((item) => item.related_product)
                .map((item) => {
                  const relatedProductData = contract.order_request.related_products?.find(
                    (rp) => rp.product.uuid === item.related_product.uuid,
                  )

                  return (
                    <Tag key={item.uuid} color="green">
                      {relatedProductData?.product?.name ?? 'Unknown Product'}
                    </Tag>
                  )
                })}
            </Space>
            {contract.order_request.items
              .filter((item) => item.related_product)
              .map((item, index, filteredItems) => (
                <div key={item.uuid}>
                  <Table
                    dataSource={item.related_product.items}
                    columns={relatedProductColumnsTranslate}
                    rowKey={(record) => record.uuid}
                    pagination={false}
                  />
                  {index < filteredItems.length - 1 && <Divider />}
                </div>
              ))}
          </>
        )}
        {contract?.order_request?.related_products &&
          contract.order_request.related_products.length !== 0 ? (
          <>
            <Divider />
            <Title
              style={{
                marginTop: '20px',
                marginBottom: '10px',
              }}
            >
              {t('RelatedProducts')}
            </Title>
            {contract.order_request.related_products.map((relatedProduct) => (
              <div key={relatedProduct.uuid}>
                <Table
                  dataSource={transformOrderRequest(relatedProduct).items}
                  columns={relatedProductsColumnsTranslate}
                  rowKey="uuid"
                  pagination={false}
                />
              </div>
            ))}
          </>
        ) : null}
      </Col>
    )
  }

  return (
    <Form.List name="items">
      {(fields, { add, remove }) => (
        <>
          <Row gutter={[8, 8]} style={{ marginBottom: '10px' }}>
            {fields.map((field, index) => (
              <>
                {renderProductCard({ ...field, remove }, index)}
                {renderContractDetails()}
              </>
            ))}
          </Row>
          {action !== 'change' && !data?.device ? (
            <Form.Item>
              <Button
                block
                type="primary"
                onClick={addNewProduct}
                data-cy="add-items"
              >
                {t('ADD_PRODUCT')}
              </Button>
            </Form.Item>
          ) : null}
        </>
      )}
    </Form.List>
  )
}

export default FormItems

export const total: CustomDescriptionType<IContract> = [
  {
    span: 1,
    label: 'TOTAL',
    key: 'total',
  },
]
